import { OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SystemMessageType } from '@psei/ng/api';
import { ButtonType } from 'projects/ppb-shared-library/src/lib/models/button-config.model';
import { ApiResponseMessages } from 'projects/ppb-shared-library/src/lib/models/global.model';
import { PpbToasterService } from 'projects/ppb-shared-library/src/lib/pseic-toaster/ppb-toaster.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import * as zxcvbn_ from 'zxcvbn';
import { UserService } from '../shared/services/user.service';

const zxcvbn = zxcvbn_ as any; // fix for rollup-error "cannot call namespace..."

export interface IZXCVBNValidatorResult {
  zxcvbn: zxcvbn_.ZXCVBNFeedback;
}

@Component({
  selector: 'ppb-password-set',
  templateUrl: './ppb-password-set.component.html',
  styleUrls: ['./ppb-password-set.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class PpbPasswordSetComponent implements OnInit, OnDestroy {

  loginForm: UntypedFormGroup;
  step: number = 1;
  passwordStrength: number = null;
  isLinkValid = false;
  errorText = '';
  isLoading$ = new BehaviorSubject(false);
  ButtonType = ButtonType;

  private subs = new Subscription();
  private newPasswordErrorSubscription: Subscription;
  private confirmPasswordErrorSubscription: Subscription;
  private uuid: string = null;
  private token: string = null;
  private toasterRef: OverlayRef;

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private cd: ChangeDetectorRef,
    private ppbToasterService: PpbToasterService
  ) {
  }

  ngOnInit(): void {
    this.uuid = this.route.snapshot.params.uuid;
    this.subs.add(
      this.userService.passwordChangeEmailTokenValidate(this.uuid)
        .subscribe((res) => {
            this.token = res.token;
            this.isLinkValid = true;
            this.cd.markForCheck();
          },
          (errorResponse) => {
            const apiResponseMessages = errorResponse.error as ApiResponseMessages;
            const skippedMessage = apiResponseMessages?.messages.find(message => [
              'msg.err.account.reset.password.email.link.account.not.active',
              'msg.err.account.reset.password.email.link.expired',
              'msg.err.account.reset.password.email.link.account.disable',
              'msg.err.account.reset.password.email.link.account.blocked'
            ].includes(message.code));

            this.isLinkValid = false;
            this.errorText = skippedMessage?.description;
            this.toasterRef = this.ppbToasterService.open(this.errorText, 'error', false);
            this.cd.detectChanges();
          })
    );

    this.loginForm = this.fb.group({
      newPassword: new UntypedFormControl('', { validators: [Validators.required] }),
      newPasswordConfirm: new UntypedFormControl('', { validators: [Validators.required] }),
      smsCode: new UntypedFormControl('', { validators: [Validators.required] })
    });

    this.newPasswordErrorSubscription = this.loginForm.controls.newPassword.valueChanges.subscribe(res => {
      const result: zxcvbn_.ZXCVBNResult = zxcvbn(res);
      this.passwordStrength = res !== '' ? result.score : null;
      const errors = this.passwordStrength >= 3
        ? null
        : {
          phone: true,
          message: $localize`:@@login.password_strength:Minimalna siła hasła musi być 3`,
          type: SystemMessageType.Negative
        };
      this.loginForm.controls.newPassword.setErrors(errors);
      this.cd.markForCheck();
    });
    this.confirmPasswordErrorSubscription = this.loginForm.controls.newPasswordConfirm.valueChanges.subscribe(res => {
      const errors = res === this.loginForm.getRawValue().newPassword
        ? null
        : {
          phone: true,
          message: $localize`:@@login.passwords_equals:Hasła muszą być takie same`,
          type: SystemMessageType.Negative
        };
      this.loginForm.controls.newPasswordConfirm.setErrors(errors);
      this.cd.markForCheck();
    });

    this.subs.add(this.newPasswordErrorSubscription);
    this.subs.add(this.confirmPasswordErrorSubscription);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  passwordChangeFirstStep(): void {
    this.loginForm.controls.newPassword.disable();
    this.loginForm.controls.newPasswordConfirm.disable();
    this.isLoading$.next(true);
    this.subs.add(
      this.userService.passwordChangeFirstStep(
        this.loginForm.getRawValue().newPassword,
        this.token
      ).subscribe(_ => {
        this.step = 2;
        this.isLoading$.next(false);
      }, _ => this.isLoading$.next(false)));
  }

  passwordChangeFinalStep(newPassword: string): void {
    this.isLoading$.next(true);
    this.subs.add(
      this.userService.passwordChangeFinalStep(
        newPassword,
        this.token,
        this.loginForm.getRawValue().smsCode
      ).subscribe(_ => {
        this.isLoading$.next(false);
        this.router.navigate(['/login'], { state: { actionParam: 'password-changed' } });
      }, _ => this.isLoading$.next(false))
    );
  }

  nextStep(step: number): void {
    const newPassword = this.loginForm.controls.newPassword;
    const newPasswordConfirm = this.loginForm.controls.newPasswordConfirm;

    switch (step) {
      case 2:
        newPassword.markAsTouched();
        newPassword.updateValueAndValidity();
        newPasswordConfirm.markAsTouched();
        newPasswordConfirm.updateValueAndValidity();
        if (newPassword.valid && newPasswordConfirm.valid) {
          this.passwordChangeFirstStep();
        }
        break;
      case 3:
        const newPasswordValue = this.loginForm.getRawValue().newPassword;
        this.newPasswordErrorSubscription.unsubscribe();
        this.confirmPasswordErrorSubscription.unsubscribe();
        newPassword.disable();
        newPasswordConfirm.disable();
        this.passwordChangeFinalStep(newPasswordValue);
        break;
      default:
        this.step = step;
        break;
    }
  }

  mainPage(): void {
    this.toasterRef?.detach();
    this.router.navigateByUrl('/login');
  }

}
